import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { addImpulse } from './prices';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Coordinated crypto pump-and-dump mini-arc.
 *
 * Player gets DM'd by crypto VC: "we're pumping $JEET tonight. you in?"
 * Three decision points over the pump cycle:
 *   1. how much to put in
 *   2. when to sell (peak vs hold for "the moon")
 *   3. when the SEC notices, what to say
 *
 * Real impulses applied to $JEET in real time. Real cash deltas.
 * Real SEC heat consequences.
 */

export function startCryptoPumpArc(): void {
  useMeta.getState().recordLore('arc-crypto-pump', 'Crypto Pump');
  useCinematic.getState().fire({
    id: `crypto-pump-${Date.now()}`,
    type: 'breaking',
    headline: 'COORDINATED PUMP — $JEET',
    subhead: 'a Telegram group of 14,000 people. you are one. your job: buy. then post. then sell. faster than them.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  // Pump begins immediately
  addImpulse({ ticker: 'JEET', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: 0.45 });

  // First decision: how much to put in
  setTimeout(() => {
    PHONE_ACTION_HANDLERS['pump-q1-small'] = () => {
      useMarkets.getState().applyFill('JEET', 'buy', 5_000, 0.0014);
      sounds.fill();
    };
    PHONE_ACTION_HANDLERS['pump-q1-medium'] = () => {
      useMarkets.getState().applyFill('JEET', 'buy', 25_000, 0.0014);
      sounds.fill();
    };
    PHONE_ACTION_HANDLERS['pump-q1-yolo'] = () => {
      const cash = useMarkets.getState().cash;
      const qty = Math.floor((cash * 0.9) / 0.0014);
      useMarkets.getState().applyFill('JEET', 'buy', qty, 0.0014);
      useLife.getState().bumpSecHeat(8);
      sounds.fill();
    };
    usePhone.getState().receiveMessage({
      threadId: 'broker', fromNpc: 'broker',
      text: 'crypto VC just hit me up. coordinated pump on $JEET in T-minus 60 seconds. how much you in for.',
      actions: [
        { id: 'a', label: 'small', effectId: 'pump-q1-small' },
        { id: 'b', label: 'medium', effectId: 'pump-q1-medium' },
        { id: 'c', label: 'YOLO', effectId: 'pump-q1-yolo' },
      ],
    });
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'pump starting — choose size' });
    sounds.notifyAlert();
  }, 5_000);

  // Second decision: when to sell (peak vs hold)
  setTimeout(() => {
    PHONE_ACTION_HANDLERS['pump-q2-sell'] = () => {
      const pos = useMarkets.getState().positions.find((p) => p.ticker === 'JEET');
      if (pos) {
        useMarkets.getState().applyFill('JEET', 'sell', pos.qty, 0.0014 * 1.45);
        sounds.bigWin();
      }
    };
    PHONE_ACTION_HANDLERS['pump-q2-hold'] = () => {
      // Hold: tighter sell window, dump comes
      useLife.getState().bumpSanity(-2);
      // Schedule post-peak dump impulse
      addImpulse({ ticker: 'JEET', startMs: Date.now() + 30_000, durationMs: 6 * 60_000, logDelta: -0.55 });
    };
    PHONE_ACTION_HANDLERS['pump-q2-pump-post'] = () => {
      useXClone.getState().composePost('$JEET to the moon. genuinely insane fundamentals. WAGMI 🚀');
      useLife.getState().bumpSecHeat(15);
      // Pump it more briefly
      addImpulse({ ticker: 'JEET', startMs: Date.now(), durationMs: 3 * 60_000, logDelta: 0.15 });
    };
    usePhone.getState().receiveMessage({
      threadId: 'broker', fromNpc: 'broker',
      text: '$JEET is +45%. peak is now. you have 30 seconds before this thing reverses.',
      actions: [
        { id: 'a', label: 'sell now', effectId: 'pump-q2-sell' },
        { id: 'b', label: 'hold for "the moon"', effectId: 'pump-q2-hold' },
        { id: 'c', label: 'post about it on Y first', effectId: 'pump-q2-pump-post' },
      ],
    });
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'pump peaking — sell or hold?' });
    sounds.notifyAlert();
  }, 240_000);

  // Third decision: SEC notices
  setTimeout(() => {
    PHONE_ACTION_HANDLERS['pump-q3-deny'] = () => {
      useLife.getState().bumpSecHeat(20);
      usePower.getState().bumpNotoriety(8);
    };
    PHONE_ACTION_HANDLERS['pump-q3-cooperate'] = () => {
      useLife.getState().bumpSecHeat(-5);
      useMarkets.getState().addCash(-15_000);
    };
    PHONE_ACTION_HANDLERS['pump-q3-flee'] = () => {
      useLife.getState().bumpFbiHeat(25);
      useLife.getState().bumpSecHeat(-15);
    };
    usePhone.getState().receiveMessage({
      threadId: 'lawyer', fromNpc: 'lawyer',
      text: 'SEC wants to talk to you about \"recent crypto activity.\" you have 24 hours. do not answer your phone. except this one. obviously.',
      actions: [
        { id: 'a', label: 'deny everything', effectId: 'pump-q3-deny' },
        { id: 'b', label: 'cooperate', effectId: 'pump-q3-cooperate' },
        { id: 'c', label: 'flee to non-extradition country', effectId: 'pump-q3-flee', destructive: true },
      ],
    });
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'lawyer: SEC wants to talk' });
    sounds.notifyAlert();
  }, 480_000);
}
